/**
 * UebergabePage - Übergabe-Workflow für beendete Betreuungen
 * Checkliste, Fortschritt, Notizen und Nachfolgebetreuer-Zuweisung
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import {
  ArrowLeft, Check, Circle, ClipboardList, Calendar,
  User, FileText, Save, Loader2, AlertTriangle,
  CheckCircle2, ArrowRightLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_COLORS = {
  nicht_gestartet: 'bg-slate-100 text-slate-600',
  in_bearbeitung: 'bg-orange-100 text-orange-700',
  abgeschlossen: 'bg-green-100 text-green-700'
};

const STATUS_LABELS = {
  nicht_gestartet: 'Nicht gestartet',
  in_bearbeitung: 'In Bearbeitung',
  abgeschlossen: 'Abgeschlossen'
};

export default function UebergabePage() {
  const navigate = useNavigate();
  const { id: klientId } = useTabParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [betreuungEnde, setBetreuungEnde] = useState('');
  const [uebergabeAn, setUebergabeAn] = useState('');
  const [notizen, setNotizen] = useState('');

  useEffect(() => { if (klientId) loadUebergabe(); }, [klientId]);

  async function loadUebergabe() {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/betreuung/klienten/${klientId}/uebergabe`);
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setBetreuungEnde(d.betreuung_ende || '');
        setUebergabeAn(d.uebergabe_an || '');
        setNotizen(d.uebergabe_notizen || '');
      }
    } catch (e) { console.error(e); toast.error('Fehler beim Laden'); }
    setLoading(false);
  }

  async function toggleCheckItem(key) {
    if (!data) return;
    const newVal = !data.checkliste[key];
    try {
      const res = await fetch(`${API_URL}/api/betreuung/klienten/${klientId}/uebergabe`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkliste: { [key]: newVal } })
      });
      if (res.ok) {
        const updated = await res.json();
        setData(updated);
        toast.success(newVal ? 'Erledigt' : 'Zurückgesetzt');
      }
    } catch (e) { toast.error('Fehler beim Speichern'); }
  }

  async function saveMetadata() {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/betreuung/klienten/${klientId}/uebergabe`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          betreuung_ende: betreuungEnde || null,
          uebergabe_an: uebergabeAn || null,
          uebergabe_notizen: notizen || null
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setData(updated);
        toast.success('Gespeichert');
      }
    } catch (e) { toast.error('Fehler beim Speichern'); }
    setSaving(false);
  }

  async function starteUebergabe() {
    try {
      const res = await fetch(`${API_URL}/api/betreuung/klienten/${klientId}/uebergabe/starten`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          betreuung_ende: betreuungEnde || null,
          uebergabe_an: uebergabeAn || null
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setData(updated);
        toast.success('Übergabe-Prozess gestartet');
      }
    } catch (e) { toast.error('Fehler'); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <p className="text-slate-500">Klient nicht gefunden</p>
      </div>
    );
  }

  const uebergabeStatus = data.uebergabe_status || 'nicht_gestartet';
  const isStarted = uebergabeStatus !== 'nicht_gestartet';

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="uebergabe-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">Übergabe-Workflow</h1>
          <p className="text-slate-500 mt-0.5">{data.klient_name}</p>
        </div>
        <Badge className={STATUS_COLORS[uebergabeStatus]} data-testid="uebergabe-status-badge">
          {STATUS_LABELS[uebergabeStatus]}
        </Badge>
      </div>

      {/* Fortschritt */}
      {isStarted && (
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Fortschritt</span>
              <span className="text-sm font-bold text-slate-800" data-testid="fortschritt-text">
                {data.fortschritt.erledigt} / {data.fortschritt.gesamt} ({data.fortschritt.prozent}%)
              </span>
            </div>
            <Progress value={data.fortschritt.prozent} className="h-3" data-testid="fortschritt-bar" />
          </CardContent>
        </Card>
      )}

      {/* Meta-Daten */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-orange-500" />
            Übergabe-Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="betreuung_ende">
                <Calendar className="w-3.5 h-3.5 inline mr-1.5" />
                Betreuungsende
              </Label>
              <Input
                id="betreuung_ende"
                type="date"
                value={betreuungEnde}
                onChange={e => setBetreuungEnde(e.target.value)}
                data-testid="input-betreuung-ende"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="uebergabe_an">
                <User className="w-3.5 h-3.5 inline mr-1.5" />
                Nachfolgebetreuer
              </Label>
              <Input
                id="uebergabe_an"
                value={uebergabeAn}
                onChange={e => setUebergabeAn(e.target.value)}
                placeholder="Name des neuen Betreuers"
                data-testid="input-uebergabe-an"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notizen">Übergabe-Notizen</Label>
            <Textarea
              id="notizen"
              value={notizen}
              onChange={e => setNotizen(e.target.value)}
              placeholder="Besonderheiten, offene Punkte, Hinweise..."
              rows={3}
              data-testid="input-uebergabe-notizen"
            />
          </div>
          <div className="flex justify-end gap-2">
            {!isStarted && (
              <Button onClick={starteUebergabe} data-testid="start-uebergabe-btn">
                <ClipboardList className="w-4 h-4 mr-2" />
                Übergabe starten
              </Button>
            )}
            {isStarted && (
              <Button onClick={saveMetadata} disabled={saving} data-testid="save-meta-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Speichern
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Checkliste */}
      {isStarted && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-500" />
              Übergabe-Checkliste
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {Object.entries(data.labels || {}).map(([key, label]) => {
                const checked = data.checkliste[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleCheckItem(key)}
                    className={`w-full flex items-center gap-3 py-3 px-2 rounded-lg transition-colors text-left hover:bg-slate-50 ${
                      checked ? 'opacity-70' : ''
                    }`}
                    data-testid={`check-${key}`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                      checked
                        ? 'bg-green-500 text-white'
                        : 'border-2 border-slate-300 text-transparent'
                    }`}>
                      <Check className="w-4 h-4" />
                    </div>
                    <span className={`text-sm ${checked ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Abschluss-Hinweis */}
      {uebergabeStatus === 'abgeschlossen' && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
              <div>
                <p className="font-medium text-green-800">Übergabe abgeschlossen</p>
                <p className="text-sm text-green-600 mt-0.5">
                  Alle Punkte der Checkliste wurden erledigt. Sie können den Status der Akte jetzt auf &quot;Beendet&quot; setzen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warnhinweis */}
      {isStarted && uebergabeStatus !== 'abgeschlossen' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-700">
                Stellen Sie sicher, dass alle Punkte erledigt sind, bevor Sie die Akte auf &quot;Beendet&quot; setzen.
                Dies ist wichtig für die korrekte Vergütungsabrechnung.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
